import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router';
import { Footer } from '../components/Footer';
import imgLogowithlcon1 from "../../assets/logo-cherry-color.webp";
import imgLogoCherry from "../../assets/logo-cherry-color.webp";
import imgWhiteLogo from "../../assets/whitelogo.png";
import imgBitmap1 from "../../assets/homepage_cheerybg.webp";
import imgRectangle2 from "../../assets/cleaningbg.webp";
import imgRectangle4 from "../../assets/hircut.webp";
import imgRectangle3 from "../../assets/beauty.webp";
import imgRectangle5 from "../../assets/childern.webp";
import imgRectangle7 from "../../assets/aboutus.webp";
import imgCherrypickedVeticon1 from "../../assets/verify.png";
import imgCherrypickedBookingicon1 from "../../assets/bookinginminutes.webp";
import imgCherrypickedClockicon1 from "../../assets/flexible.webp";
import imgCherrypickedPortfolioicon1 from "../../assets/browse.webp";
import imgBitmap1Service from "../../assets/standardclean.webp";
import imgBitmap2Service from "../../assets/deepcleaan.webp";
import imgBitmap3Service from "../../assets/endoftenancy.webp";
import { FeaturedReviews } from '../components/FeaturedReviews';
import { HeroBackground } from '../components/HeroBackground';
import { PrimaryButton } from '../components/PrimaryButton';
import { SecondaryButton } from '../components/SecondaryButton';
import {
  LocationAutocomplete,
  type SelectedPlace,
} from '../components/LocationAutocomplete';
import { ServiceDropdown } from '../components/ServiceDropdown';
import {
  formatStartingFromLine,
  getPublicServices,
  getServiceCategories,
  type PublicService,
  type ServiceCategory,
} from '../../lib/site-api';
import { patchBookingDraft } from '../../lib/booking-draft';
import { useSiteImages } from '../../lib/site-images';

// Service codes whose dynamic "Starting from £X/hr" prices populate
// the landing cards. Kept on a constant so a backend rename surfaces
// here as a missing key during type-check rather than a silent revert
// to the fallback prices.
const CLEANING_SERVICE_CODES = [
  'CLEAN_STANDARD',
  'CLEAN_DEEP',
  'CLEAN_END_OF_TENANCY',
] as const;

// Hero banner slides are admin-managed (Site Images → hero_1..3) and
// resolved at runtime via useSiteImages(); imgBitmap1 is the bundled
// fallback used when no hero slides have been uploaded.

export default function LandingPage() {
  const navigate = useNavigate();
  const [service, setService] = useState('');
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  // Category tile image now comes from the category itself (managed in
  // the admin Service Categories editor), falling back to the bundled
  // asset when a category has no image set.
  const categoryImg = (code: string, fallback: string) =>
    categories.find((c) => c.code === code)?.image_url_preview_url || fallback;
  const [publicServices, setPublicServices] = useState<PublicService[]>([]);
  const [address, setAddress] = useState('');
  // Holds the OSM-resolved place when the customer picks a suggestion
  // from the autocomplete dropdown. Without this the lat/lng were
  // thrown away and /booking-location would render the "pick from the
  // suggestions" error even though the customer already had — they'd
  // have to re-type and re-pick.
  const [place, setPlace] = useState<SelectedPlace | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  // Admin-managed landing images (Site Images), with bundled fallbacks.
  const { resolve, heroList } = useSiteImages();
  const heroImages = heroList([imgBitmap1]);
  // Optional admin-managed background for the gift-card promo banner
  // (Site Images → Gift card promo). Empty string = use the gradient.
  const giftCardImg = resolve('gift_card', '');
  // Optional dedicated mobile artwork (taller crop). Falls back to the
  // wide desktop banner when not set.
  const giftCardMobileImg = resolve('gift_card_mobile', '');

  // Looks up a service by code and returns its "Starting from £X/hr"
  // line, falling back to a sensible static line so the card never
  // renders an empty price box while the request is in flight.
  const priceLineFor = (code: string, fallback: string): string => {
    const svc = publicServices.find((s) => s.code === code);
    return svc ? formatStartingFromLine(svc) : fallback;
  };

  // Cleaning category card shows the cheapest of the three cleaning
  // services so it lines up with the per-service cards below.
  const cleaningStartingPrice = (fallback: string): string => {
    const cleaningRates = CLEANING_SERVICE_CODES.map(
      (code) =>
        publicServices.find((s) => s.code === code)?.starting_from_price,
    ).filter((v): v is number => typeof v === 'number');
    if (cleaningRates.length === 0) return fallback;
    const min = Math.min(...cleaningRates);
    const display = min % 1 === 0 ? min.toFixed(0) : min.toFixed(2);
    return `Starting from £${display}/hr`;
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    let cancelled = false;
    // Pulls the dynamic "Starting from £X" prices so the cleaning
    // category + service cards reflect the lowest active pro rate.
    getPublicServices()
      .then((res) => {
        if (!cancelled) setPublicServices(res.data ?? []);
      })
      .catch(() => {
        // Cards just fall back to their static "Starting from" line.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    getServiceCategories()
      .then((res) => {
        if (cancelled) return;
        const list = res.data ?? [];
        setCategories(list);
        if (list.length > 0) setService((s) => (s ? s : list[0].name));
      })
      .catch(() => {
        // Fail silently — dropdown stays empty rather than crashing the page.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Persist the hero dropdown + address onto the booking draft. When
  // the customer picked a result from the autocomplete dropdown
  // (`place` is set), carry the lat/lng through too — /booking-location
  // will detect the pre-geocoded address and let them Next straight on
  // instead of showing "Please pick your address from the suggestions".
  // If they typed without picking, lat/lng stay null and the next page
  // re-prompts.
  const handleBookNow = () => {
    const cat = categories.find((c) => c.name === service);
    const geocoded =
      place &&
      place.latitude != null &&
      place.longitude != null &&
      place.description === address;
    patchBookingDraft({
      service_category_id: cat?.id ?? null,
      meta: { ...(cat?.code ? { category_code: cat.code } : {}) },
      ...(address
        ? {
            address: {
              address_line1: geocoded ? place!.description : address,
              address_line2: null,
              city: geocoded ? (place!.city ?? null) : null,
              postcode: geocoded ? (place!.postcode ?? null) : null,
              country_code: geocoded ? (place!.country_code ?? null) : null,
              latitude: geocoded ? (place!.latitude ?? null) : null,
              longitude: geocoded ? (place!.longitude ?? null) : null,
            },
          }
        : {}),
    });
    navigate('/booking-location', { state: { address } });
  };

  // Service-grid tile click: same idea, just no address yet.
  //
  // The matcher normalises "&" → "and" and strips non-alphanumerics
  // so the display string ("Beauty & Wellness") still matches a
  // backend row named "Beauty and Wellness", "beauty wellness", etc.
  // A strict toLowerCase() compare failed silently for the Beauty,
  // Hair and Childcare tiles and the bookings all fell back to the
  // cleaning flow because service_category_id ended up null.
  const normaliseName = (s: string): string =>
    s
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  const handleCategoryTileClick = (categoryName: string) => {
    const target = normaliseName(categoryName);
    const cat = categories.find((c) => normaliseName(c.name) === target);
    patchBookingDraft({
      service_category_id: cat?.id ?? null,
      meta: { ...(cat?.code ? { category_code: cat.code } : {}) },
    });
    navigate('/booking-location');
  };

  // "Popular at-home service" card click — seed the draft with the picked
  // service (+ its category) and drop the customer into the booking flow.
  // The wizard pre-selects this service from draft.service_id, so they
  // land straight on it rather than the category's first service.
  const handleServiceCardClick = (svc: PublicService | undefined) => {
    if (svc) {
      patchBookingDraft({
        service_category_id: svc.category?.id ?? null,
        service_id: svc.id,
        meta: {
          ...(svc.category?.code ? { category_code: svc.category.code } : {}),
        },
      });
    }
    navigate('/booking-location');
  };

  return (
    <div className=" bg-white">
      {/* Navigation Bar */}
      <nav className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-300 py-4 px-4 md:px-8 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
        }`}>
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          {/* Logo */}
          <div className="w-[80px] md:w-[120px] h-[50px] md:h-[73px]">
            <img src={imgLogowithlcon1} alt="Cherry Picked" className="w-full h-full object-contain" />
          </div>

          {/* Navigation Buttons */}
          {/* Sign-up was previously a separate button — collapsed into
              "Sign In" so the nav has one auth CTA. The sign-in page
              itself surfaces "Create account" for new users. Become-a-
              Pro stays as a direct pro-signup CTA because that label
              already commits to a role and skips the chooser. */}
          <div className="flex items-center gap-2 md:gap-3">
            <Link to="/signin/customer">
              <SecondaryButton
                className="bg-white !h-[36px] md:!h-[44px] !pl-2 !pr-4 md:!pl-2 md:!pr-4 !rounded-[8px]"
                icon={
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect width="32" height="32" rx="10" fill="white" />
                    <rect width="32" height="32" rx="10" fill="#3A3335" fill-opacity="0.04" />
                    <path d="M12.0273 18.3555C13.4102 17.793 14.7344 17.5117 16 17.5117C17.2656 17.5117 18.5781 17.793 19.9375 18.3555C21.3203 18.8945 22.0117 19.6094 22.0117 20.5V22.0117H9.98828V20.5C9.98828 19.6094 10.668 18.8945 12.0273 18.3555ZM18.1094 15.1211C17.5234 15.707 16.8203 16 16 16C15.1797 16 14.4766 15.707 13.8906 15.1211C13.3047 14.5352 13.0117 13.832 13.0117 13.0117C13.0117 12.1914 13.3047 11.4883 13.8906 10.9023C14.4766 10.293 15.1797 9.98828 16 9.98828C16.8203 9.98828 17.5234 10.293 18.1094 10.9023C18.6953 11.4883 18.9883 12.1914 18.9883 13.0117C18.9883 13.832 18.6953 14.5352 18.1094 15.1211Z" fill="currentColor" />
                  </svg>
                }
              >
                Sign In
              </SecondaryButton>
            </Link>
            <Link to="/professional/signup">
              <PrimaryButton className="!h-[36px] md:!h-[44px] !px-3 md:!px-8 !rounded-[8px]">
                Become a Pro
              </PrimaryButton>
            </Link>
          </div>
        </div>
      </nav>

      {/* Header/Hero Section */}
      <header className="relative overflow-hidden bg-[#8e2a2f] flex items-center justify-center py-5 pt-20 md:pt-30 md:pb-20 px-4">
        {/* Hero background — branded gradient placeholder + fade-in
            photos so there's no flat-colour flash while images load. */}
        <HeroBackground images={heroImages} />

        {/* Hero Content - Centered Card */}
        <div className="relative z-10 w-full max-w-[555px] mx-auto">
          <div className="bg-[#8e2a2f]/80 rounded-[12px] p-6 md:p-12 shadow-2xl w-full min-h-[500px] md:min-h-[579px] flex flex-col items-center">
            {/* Logo */}
            <div className="flex justify-center mb-3">
              <div className="w-[185px]">
                <img src={imgWhiteLogo} alt="Cherry Picked" className="w-full h-auto object-contain" />
              </div>
            </div>

            {/* Title */}
            <h1 className="font-['Playfair_Display',sans-serif] font-bold text-[#fefbf2] text-[26px] md:text-[34px] text-center leading-[1.1] tracking-[-0.2px] mb-6 md:mb-8">
              Book trusted professionals in just a few clicks.
            </h1>

            {/* Features - 2 rows */}
            <div className="w-full space-y-4 mb-5">
              {/* Row 1 */}
              <div className="flex flex-wrap items-center justify-center gap-3 md:gap-6">
                {[
                  { text: 'ID Verified' },
                  { text: 'Real-time availability' },
                  { text: 'Insured' },
                ].map((feature, index) => (
                  <div key={index} className="flex items-center gap-1.5">
                    <span className="material-icons text-white !text-[14px] md:!text-[16px]">check_circle_outline</span>
                    <span className="font-['DM_Sans',sans-serif] text-white text-[12px] md:text-sm whitespace-nowrap">{feature.text}</span>
                  </div>
                ))}
              </div>
              {/* Row 2 */}
              <div className="flex flex-wrap items-center justify-center gap-3 md:gap-6">
                {[
                  { text: 'Evenings & weekends' },
                  { text: 'Book with confidence' },
                ].map((feature, index) => (
                  <div key={index} className="flex items-center gap-1.5">
                    <span className="material-icons text-white !text-[14px] md:!text-[16px]">check_circle_outline</span>
                    <span className="font-['DM_Sans',sans-serif] text-white text-[12px] md:text-sm whitespace-nowrap">{feature.text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Form Fields */}
            <div className="w-full space-y-4">
              {/* Service Select */}
              <ServiceDropdown
                label="Select a service"
                value={service || 'Loading services…'}
                onChange={setService}
                options={categories.map((c) => c.name)}
              />

              {/* Address Input (Floating Style) */}
              <LocationAutocomplete
                label="Enter your full address"
                value={address}
                onChange={(value) => {
                  setAddress(value);
                  // Free-typing invalidates the previously-picked place
                  // — drop it so we don't carry stale coordinates that
                  // don't match the current text into the next page.
                  if (place && value !== place.description) setPlace(null);
                }}
                onPlaceSelected={(p) => {
                  setPlace(p);
                  setAddress(p.description);
                }}
                placeholder="Start typing the first line of your address"
                className="w-full"
                containerHeight="h-[64px]"
                icon="location_on"
              />

              {/* Book Now Button */}
              <button
                onClick={handleBookNow}
                className="w-full h-[54px] bg-[#f2eee2] hover:bg-[#ebe5d5] text-[#8e2a2f] font-['DM_Sans',sans-serif] font-bold text-[14px] rounded-[8px] transition-colors mt-2"
              >
                Book now
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Features Section */}
      <section className="pt-[50px] pb-[50px] bg-[#f2eee2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: imgCherrypickedVeticon1,
                title: 'Vetted Professionals',
                desc: ['Carefully selected experts you', 'can trust.']
              },
              {
                icon: imgCherrypickedBookingicon1,
                title: 'Book in minutes',
                desc: ['Find and secure appointments', 'quickly.']
              },
              {
                icon: imgCherrypickedClockicon1,
                title: 'Flexible Appointments',
                desc: ['Choose times that fit around', 'your schedule.']
              },
              {
                icon: imgCherrypickedPortfolioicon1,
                title: 'Browse Portfolios & Reviews',
                desc: ['See previous work and real', 'customer feedback.']
              },
            ].map((feature, index) => (
              <div key={index} className="bg-white rounded-[6px] shadow-[0px_3px_4px_0px_rgba(153,155,168,0.15)] p-8 text-center">
                <div className="w-[51px] h-[51px] mx-auto mb-6">
                  <img src={feature.icon} alt={feature.title} className="w-full h-full object-contain" />
                </div>
                <h3 className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-lg leading-[27px] mb-3">
                  {feature.title}
                </h3>
                <div className="font-['DM_Sans',sans-serif] text-[#3a3335] text-sm leading-[21px] tracking-[-0.1px]" style={{ fontVariationSettings: "'opsz' 14" }}>
                  {feature.desc.map((line, i) => (
                    <p key={i} className={i < feature.desc.length - 1 ? 'mb-px' : ''}>
                      {line}
                    </p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-5 md:py-0 bg-[#f2eee2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              { img: categoryImg('CLEANING', imgRectangle2), title: 'Cleaning', desc: 'Domestic cleaning including standard clean, deep clean and end of tenancy.', price: cleaningStartingPrice('Starting from £21.00/hr') },
              { img: categoryImg('HAIR', imgRectangle4), title: 'Hair', desc: 'At-home hair services.', price: '10% off with Cherry Picked membership' },
              { img: categoryImg('BEAUTY_WELLNESS', imgRectangle3), title: 'Beauty & Wellness', desc: 'At-home beauty and wellness treatments.', price: '10% off with Cherry Picked membership' },
              { img: categoryImg('CHILDCARE', imgRectangle5), title: 'Childcare', desc: 'At-home childcare services.', price: '10% off with Cherry Picked membership' },
            ].map((service, index) => (
              <div key={index} className="relative h-[470px] overflow-hidden group cursor-pointer bg-[#d9d9d9]">
                <img src={service.img} alt={service.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent to-white" style={{ top: '62px' }} />
                <div className="absolute bottom-0 left-0 right-0 text-center px-8 pb-16">
                  <h3 className="font-['Playfair_Display',sans-serif] font-extrabold text-[34px] leading-[48px] tracking-[-0.0944px] text-[#8e2a2f] mb-3">
                    {service.title}
                  </h3>
                  <div className="font-['DM_Sans',sans-serif] text-sm leading-[21px] tracking-[-0.1px] text-[#3a3335] mb-6" style={{ fontVariationSettings: "'opsz' 14" }}>
                    <p className="mb-px">{service.desc}</p>
                    <p>{service.price}</p>
                  </div>
                  <PrimaryButton
                    onClick={() => handleCategoryTileClick(service.title)}
                    className="!w-[151px] !h-[40px] !text-xs !uppercase !tracking-wider !rounded-[4px] mx-auto"
                  >
                    BOOK NOW
                  </PrimaryButton>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gift Card Section — a single admin-managed banner image (Site
          Images → Gift card promo), clickable to the gift-card flow. The
          whole banner is the uploaded artwork; it hides until an admin
          sets an image. */}
      {giftCardImg || giftCardMobileImg ? (
        <section className="py-16 bg-[#f2eee2]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <button
              type="button"
              onClick={() => navigate('/gift-card')}
              aria-label="Send a Cherry Picked gift card"
              className="block w-full cursor-pointer"
            >
              {/* Desktop / tablet — the wide banner. */}
              <img
                src={giftCardImg || giftCardMobileImg}
                alt=""
                style={{ aspectRatio: giftCardImg ? '1184 / 320' : '4 / 5' }}
                className="hidden sm:block w-full object-cover rounded-[20px]"
              />
              {/* Mobile — the dedicated tall image when set, otherwise
                  the desktop image so the banner still shows. */}
              <img
                src={giftCardMobileImg || giftCardImg}
                alt=""
                style={{
                  aspectRatio: giftCardMobileImg ? '4 / 5' : '1184 / 320',
                }}
                className="block sm:hidden w-full object-cover rounded-[20px]"
              />
            </button>
          </div>
        </section>
      ) : null}

      {/* Reviews Section — admin-curated featured reviews */}
      <FeaturedReviews />

      {/* About Section */}
      <section className=" overflow-hidden bg-[#f2eee2]  py-16 md:py-16">
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className="grid grid-cols-1 lg:grid-cols-2">
            {/* Left: Image (Square corners) */}
            <div className="h-[400px] lg:h-auto overflow-hidden">
              <img src={resolve('about_us', imgRectangle7)} alt="About us" className="w-full h-full object-cover" />
            </div>

            {/* Right: Content (Centered) */}
            <div className="flex items-center justify-center py-16 px-8 md:px-16 lg:px-24 bg-white">
              <div className="max-w-[420px] text-center">
                {/* Logo */}
                <div className="flex justify-center mb-6">
                  <img src={imgLogoCherry} alt="Cherry Picked" className="h-[70px] object-contain" />
                </div>
                <h2 className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[34px] md:text-[40px] leading-tight mb-6">
                  About us
                </h2>
                <p className="font-['DM_Sans',sans-serif] text-[#3a3335] text-[15px] leading-relaxed" style={{ fontVariationSettings: "'opsz' 14" }}>
                  Cherry Picked was founded by three busy mums who understood first-hand the challenge of balancing family life, careers, home and self-care. Between packed schedules and endless to-do lists, we saw the need for a simpler, more reliable way to access exceptional local services.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Popular at-home services Section — admin-curated. Admins flag up
          to 3 services via the "Popular" toggle on the admin Services
          list; the band shows ONLY those services and hides when none
          are flagged. */}
      {(() => {
        const popularServices = publicServices
          .filter((s) => s.is_popular)
          .slice(0, 3);
        if (popularServices.length === 0) return null;
        return (
          <section className="relative bg-[#f2eee2] pt-[120px] pb-[50px]">
            {/* Burgundy Background Shape */}
            <div className="absolute top-0 left-0 right-0 h-[245px] bg-[#8e2a2f] overflow-hidden">
              <div className="absolute inset-0" style={{
                background: '#8e2a2f',
                clipPath: 'polygon(0 0, 100% 0, 100% 60%, 0 100%)'
              }} />
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
              {/* Header */}
              <div className="flex items-center justify-between mb-12">
                <h2 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#eee] text-[34px] leading-[48px] tracking-[-0.0944px]">
                  Popular at-home services
                </h2>
              </div>

              {/* Cards — each is a flagged service. Title + live price come
                  from the public catalogue; the image is the service's own
                  catalogue image. Clicking starts a booking for it. */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {popularServices.map((svc) => {
                  // Prefer the dedicated wide Popular image; fall back to
                  // the square catalogue image, then the bundled asset.
                  const img =
                    svc.popular_image_preview_url ??
                    svc.popular_image ??
                    svc.image_preview_url ??
                    svc.image ??
                    imgBitmap1Service;
                  return (
                    <div
                      key={svc.id}
                      onClick={() => handleServiceCardClick(svc)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleServiceCardClick(svc);
                        }
                      }}
                      className="bg-white rounded-[6px] shadow-[0px_3px_4px_0px_rgba(153,155,168,0.15)] overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
                    >
                      <div className="relative h-[188px] bg-white overflow-hidden">
                        <div className="absolute inset-0 bg-black/20" />
                        <img src={img} alt={svc.name} className="absolute inset-0 w-full h-full object-cover" />
                      </div>
                      <div className="p-6">
                        <h3 className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-lg leading-[27px] mb-1">
                          {svc.name}
                        </h3>
                        <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-sm leading-[21px] tracking-[-0.1px]" style={{ fontVariationSettings: "'opsz' 14" }}>
                          {formatStartingFromLine(svc)}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        );
      })()}

      <Footer />
    </div>
  );
}
